import { useEffect, useState } from 'react';

import AdminLayout from '../../components/admin/AdminLayout';
import { ResponsiveTable } from '../../components/admin/responsive';
import { api } from '../../services/api';

function Badge({ children, bg, color, border }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: '0.08em',
        padding: '3px 10px',
        borderRadius: 99,
        background: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      {children}
    </span>
  );
}

// Alinhado aos mesmos 3 tokens de status usados no dashboard principal:
// verde sucesso / damasco pendente-atenção / parchment-graphite neutro
// (ver src/index.css e src/design/tokens.css). ACTIVE mantém o verde
// semântico; OVERDUE (inadimplente) usa o damasco de atenção em vez do
// vermelho genérico; os demais estados neutros caem em parchment/graphite.
function StatusBadge({ status }) {
  const map = {
    ACTIVE: {
      label: 'Ativa',
      bg: '#dcfce7',
      color: '#166534',
      border: '#bbf7d0',
    },
    INACTIVE: {
      label: 'Inativa',
      bg: 'var(--color-parchment)',
      color: 'var(--color-graphite)',
      border: 'var(--color-linen)',
    },
    OVERDUE: {
      label: 'Inadimplente',
      bg: 'var(--color-h-damasco-wash)',
      color: 'var(--color-h-damasco-text)',
      border: 'var(--color-h-damasco-soft)',
    },
    CANCELLED: {
      label: 'Cancelada',
      bg: 'var(--color-parchment)',
      color: 'var(--color-graphite)',
      border: 'var(--color-linen)',
    },
    EXPIRED: {
      label: 'Expirada',
      bg: 'var(--color-parchment)',
      color: 'var(--color-graphite)',
      border: 'var(--color-linen)',
    },
  };
  const s = map[status] ?? {
    label: status ?? '—',
    bg: 'var(--color-parchment)',
    color: 'var(--color-graphite)',
    border: 'var(--color-linen)',
  };
  return (
    <Badge bg={s.bg} color={s.color} border={s.border}>
      {s.label}
    </Badge>
  );
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

export default function Assinantes() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [modal, setModal] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    email: '',
    cpf: '',
    valor: 7499,
    nextDueDate: new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      1,
    )
      .toISOString()
      .split('T')[0],
    descricao: 'Lily Member — Mensalidade',
  });

  async function carregar() {
    try {
      const { data } = await api.get('/admin/assinantes');
      setDados(data);
    } catch {
      setFeedback({ msg: 'Erro ao carregar assinantes.', tipo: 'erro' });
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  function mostrarFeedback(msg, tipo = 'ok') {
    setFeedback({ msg, tipo });
    setTimeout(() => setFeedback(null), 5000);
  }

  async function criarAssinante(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      const { data } = await api.post('/admin/assinantes', form);
      mostrarFeedback(data.mensagem);
      setModal(false);
      carregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao criar assinante.',
        'erro',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function cancelar(id, nome) {
    if (!confirm(`Cancelar assinatura de ${nome}? O Asaas parará de cobrar.`))
      return;
    try {
      await api.delete(`/admin/assinantes/${id}`);
      mostrarFeedback('Assinatura cancelada.');
      carregar();
    } catch (err) {
      mostrarFeedback(err.response?.data?.error || 'Erro ao cancelar.', 'erro');
    }
  }

  const formatarCPF = (v) =>
    v.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

  return (
    <AdminLayout>
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>Assinantes Recorrentes</h1>
          <p style={S.subtitle}>
            Cobrança mensal automática via Asaas · Asaas envia o boleto/PIX por
            email
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={async () => {
              try {
                const { data } = await api.post(
                  '/admin/assinantes/email-teste',
                );
                mostrarFeedback(data.mensagem);
              } catch (err) {
                mostrarFeedback(
                  err.response?.data?.error || 'Erro ao enviar email de teste.',
                  'erro',
                );
              }
            }}
            style={{ ...S.btnSecondary, fontSize: 12 }}
          >
            ✉ Testar email
          </button>
          <button onClick={() => setModal(true)} style={S.btnPrimary}>
            + Nova Assinante
          </button>
        </div>
      </div>

      {feedback && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            marginBottom: 16,
            fontSize: 13,
            fontWeight: 700,
            background: feedback.tipo === 'erro' ? '#fee2e2' : '#dcfce7',
            color: feedback.tipo === 'erro' ? '#991b1b' : '#166534',
          }}
        >
          {feedback.msg}
        </div>
      )}

      {/* Métricas */}
      {dados && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          <div style={S.metricCard}>
            <div style={S.metricLabel}>Total assinantes</div>
            <div style={S.metricValue}>{dados.total}</div>
          </div>
          <div style={S.metricCard}>
            <div style={S.metricLabel}>Assinaturas ativas</div>
            <div style={{ ...S.metricValue, color: '#166534' }}>
              {dados.ativos}
            </div>
          </div>
          <div style={S.metricCard}>
            <div style={S.metricLabel}>Receita mensal estimada</div>
            <div style={{ ...S.metricValue, color: '#7c3aed' }}>
              {(
                (dados.assinantes?.filter(
                  (a) => a.asaasStatus === 'ACTIVE' || a.status === 'ACTIVE',
                ).length ?? 0) * 74.99
              ).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
          </div>
        </div>
      )}

      <div style={S.infoBox}>
        <strong>Como funciona:</strong> Ao cadastrar uma assinante, o Asaas
        criará uma cobrança PIX automática todo mês na data configurada. O email
        de cobrança chegará direto para a membro. Você acompanha quem pagou aqui
        no painel.
      </div>

      {carregando ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>
          Carregando...
        </div>
      ) : (
        <div style={S.card}>
          <ResponsiveTable
            data={dados.assinantes}
            columns={[
              {
                key: 'usuario.name',
                label: 'Membro',
                render: (_, row) => (
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>
                    {row.usuario?.name ?? '—'}
                  </div>
                ),
              },
              {
                key: 'usuario.email',
                label: 'Email',
                render: (_, row) => (
                  <div style={{ fontSize: 12 }}>{row.usuario?.email}</div>
                ),
              },
              {
                key: 'proximaCobranca',
                label: 'Próx. cobrança',
                render: (v) => fmt(v),
              },
              {
                key: 'asaasStatus',
                label: 'Status Asaas',
                render: (_, row) => (
                  <StatusBadge status={row.asaasStatus ?? row.status} />
                ),
              },
              {
                key: 'ultimoPagamento',
                label: 'Último pagamento',
                render: (v) => {
                  if (!v) return '—';
                  return (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>
                        {(v.amountInCents / 100).toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                      </div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>
                        {fmt(v.createdAt)}
                      </div>
                    </div>
                  );
                },
              },
              {
                key: 'acoes',
                label: 'Ações',
                render: (_, row) => (
                  <button
                    onClick={() => cancelar(row.id, row.usuario?.name)}
                    style={{
                      fontSize: 11,
                      color: '#dc2626',
                      background: '#fee2e2',
                      border: '1px solid #fecaca',
                      borderRadius: 99,
                      padding: '4px 10px',
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    Cancelar
                  </button>
                ),
              },
            ]}
            options={{
              emptyMessage: 'Nenhuma assinante cadastrada ainda',
            }}
          />
        </div>
      )}
      {modal && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h2 style={S.modalTitle}>Cadastrar nova assinante</h2>
            <p
              style={{
                fontSize: 13,
                color: '#64748b',
                marginBottom: 20,
                marginTop: -12,
              }}
            >
              O Asaas enviará um PIX de cobrança automática todo mês para o
              email da membro.
            </p>
            <form
              onSubmit={criarAssinante}
              style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                }}
              >
                <label style={S.label}>
                  Nome
                  <input
                    required
                    style={S.input}
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    placeholder="Nome completo"
                  />
                </label>
                <label style={S.label}>
                  Email
                  <input
                    required
                    type="email"
                    style={S.input}
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    placeholder="email@exemplo.com"
                  />
                </label>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                }}
              >
                <label style={S.label}>
                  CPF
                  <input
                    required
                    style={S.input}
                    value={form.cpf}
                    onChange={(e) =>
                      setForm({ ...form, cpf: formatarCPF(e.target.value) })
                    }
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                </label>
                <label style={S.label}>
                  Valor mensal (centavos)
                  <input
                    required
                    type="number"
                    style={S.input}
                    value={form.valor}
                    onChange={(e) =>
                      setForm({ ...form, valor: Number(e.target.value) })
                    }
                  />
                </label>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                }}
              >
                <label style={S.label}>
                  Primeira cobrança
                  <input
                    required
                    type="date"
                    style={S.input}
                    value={form.nextDueDate}
                    onChange={(e) =>
                      setForm({ ...form, nextDueDate: e.target.value })
                    }
                  />
                </label>
                <label style={S.label}>
                  Descrição
                  <input
                    style={S.input}
                    value={form.descricao}
                    onChange={(e) =>
                      setForm({ ...form, descricao: e.target.value })
                    }
                  />
                </label>
              </div>
              <div
                style={{
                  background: '#fff5f7',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontSize: 12,
                  color: '#e8336d',
                  fontWeight: 600,
                }}
              >
                Valor:{' '}
                <strong>
                  {(form.valor / 100).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                  /mês
                </strong>{' '}
                via PIX · Cobrança automática todo mês
              </div>

              {/* Termos de assinatura */}
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '12px 14px',
                  fontSize: 12,
                  color: '#64748b',
                  lineHeight: 1.6,
                }}
              >
                <p
                  style={{
                    fontWeight: 800,
                    color: '#334155',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginBottom: 8,
                  }}
                >
                  Termos da assinatura
                </p>
                <ul
                  style={{
                    margin: 0,
                    padding: 0,
                    listStyle: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <li>
                    • A membro será notificada por email pelo Asaas todo mês
                    para efetuar o pagamento via PIX.
                  </li>
                  <li>
                    • Cancelamento com até 5 dias de antecedência da próxima
                    cobrança via WhatsApp.
                  </li>
                  <li>
                    • Sem reembolso proporcional após cobrança do mês vigente.
                  </li>
                </ul>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginTop: 10,
                    cursor: 'pointer',
                    fontWeight: 700,
                    color: '#334155',
                  }}
                >
                  <input
                    type="checkbox"
                    required
                    style={{
                      marginTop: 2,
                      accentColor: '#ff2d6c',
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    Confirmo que a membro foi informada e concorda com os termos
                    acima.
                  </span>
                </label>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  justifyContent: 'flex-end',
                  marginTop: 4,
                }}
              >
                <button
                  type="button"
                  onClick={() => setModal(false)}
                  style={S.btnSecondary}
                >
                  Cancelar
                </button>
                <button type="submit" disabled={salvando} style={S.btnPrimary}>
                  {salvando ? 'Criando...' : 'Criar assinatura'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const S = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 12,
  },
  h1: { fontSize: 22, fontWeight: 900, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: 13, color: '#94a3b8', margin: '4px 0 0' },
  metricCard: {
    background: '#fff',
    borderRadius: 12,
    padding: '14px 20px',
    border: '1px solid #f1f5f9',
    flex: 1,
  },
  metricLabel: {
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 4,
  },
  metricValue: { fontSize: 24, fontWeight: 900, color: '#0f172a' },
  infoBox: {
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 13,
    color: '#0369a1',
    marginBottom: 20,
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #f1f5f9',
    overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    color: '#94a3b8',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    borderBottom: '1px solid #f1f5f9',
    background: '#fafafa',
  },
  td: {
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f8fafc',
  },
  btnPrimary: {
    padding: '9px 18px',
    background: '#ff2d6c',
    color: '#fff',
    border: 'none',
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
    letterSpacing: '0.05em',
  },
  btnSecondary: {
    padding: '9px 18px',
    background: '#f8fafc',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  modal: {
    background: '#fff',
    borderRadius: 20,
    padding: 32,
    width: '100%',
    maxWidth: 560,
    boxShadow: '0 20px 60px rgba(0,0,0,.15)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 900,
    color: '#0f172a',
    marginBottom: 6,
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    fontSize: 11,
    fontWeight: 900,
    color: '#64748b',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  input: {
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 14,
    color: '#0f172a',
    outline: 'none',
    fontFamily: 'inherit',
  },
};
